import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {
    GetUsers_users_edges_node as User,
    GetUsers as GetUsersQuery,
    GetUsersVariables as GetUsersQueryVariables,
} from 'nutshell-graphql-types';

import GET_USERS from '../queries/get-users.graphql';

const LIMIT = 50; // Fetch in reasonable batches

export const useGetUsers = (
    search?: string | null
): {
    isLoading: boolean;
    errorMessage: string | null | undefined;
    users: User[];
} => {
    const {loading, data, error, fetchMore, networkStatus} = useQuery<
        GetUsersQuery,
        GetUsersQueryVariables
    >(GET_USERS, {
        variables: {first: LIMIT, search},
        notifyOnNetworkStatusChange: true,
    });

    const users = data && data.users ? data.users : null;
    const pageInfo = users ? users.pageInfo : null;
    const hasNextPage = pageInfo ? pageInfo.hasNextPage : false;
    const endCursor = pageInfo ? pageInfo.endCursor : null;

    // Only show loading for initial fetch (networkStatus 1), not for fetchMore (networkStatus 3)
    const isInitialLoading = networkStatus === 1;

    // Automatically fetch all remaining users in the background
    React.useEffect(() => {
        if (users && hasNextPage && !loading) {
            fetchMore({
                query: GET_USERS,
                variables: {
                    first: LIMIT,
                    after: endCursor,
                },
                updateQuery: (previousResult, {fetchMoreResult}) => {
                    if (
                        !fetchMoreResult ||
                        !fetchMoreResult.users.edges.length ||
                        !previousResult
                    ) {
                        return previousResult;
                    }

                    return {
                        users: {
                            __typename: 'UsersConnection',
                            pageInfo: fetchMoreResult.users.pageInfo,
                            edges: [...previousResult.users.edges, ...fetchMoreResult.users.edges],
                        },
                    };
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, hasNextPage, loading]);

    return {
        isLoading: isInitialLoading,
        // @ts-expect-error
        errorMessage: safelyGetGraphQLErrorMessage(error),
        users: users ? users.edges.map((edge) => edge.node) : [],
    };
};
